import { shallowEqual, useSelector } from '@xstate/react'
import clsx from 'clsx'
import { useEffect } from 'preact/hooks'

import { AppProps } from '../app'
import { Interactions, trackingService } from '../machines/tracking.machine'
import { Button } from './Button'
import { EmailInput } from './EmailInput'
import { emailPayloadSelector, emailInputSelector, useNaturalPowerContext } from './NaturalPowerCalculatorProvider'
import { StatusMessage } from './StatusMessage'

interface ActionsProps extends Pick<AppProps, 'contactCallback'> {
  className?: string
}

export function Actions({ className, contactCallback }: ActionsProps) {
  const services = useNaturalPowerContext()
  const { naturalPowerRatio, ...emailTemplateModel } = useSelector(
    services.costInput,
    emailPayloadSelector,
    shallowEqual,
  )
  const { emailAddress, message, successful, enabled, valid, sending, canRetry } = useSelector(
    services.emailReport,
    emailInputSelector,
    shallowEqual,
  )

  const getPayload = () => ({
    toEmail: emailAddress,
    emailTemplateModel,
  })

  const contributing = naturalPowerRatio > 0
  const handleClickContact = () => {
    trackingService.send({ type: 'Interact', name: Interactions.contactButton })
    contactCallback && contactCallback()
  }

  useEffect(() => {
    contributing ? services.emailReport.send({ type: 'Enable' }) : services.emailReport.send({ type: 'Disable' })
  }, [contributing, services.emailReport])

  const handleChangeEmail = (value: string) => {
    services.emailReport.send({ type: 'Update email address', value })
  }

  const handleFocusEmail = () => {
    trackingService.send({ type: 'Interact', name: Interactions.emailAddress })
  }

  const handleBlurEmail = () => {
    services.emailReport.send({ type: 'Mark as dirty' })
  }

  const handleSubmitEmail = async () => {
    if (valid) {
      services.emailReport.send({
        type: 'Email results',
        payload: getPayload(),
      })
    } else {
      services.emailReport.send({ type: 'Submit whilst invalid' })
    }
  }

  const onRetry = canRetry
    ? () => {
        services.emailReport.send({ type: 'Retry' })
      }
    : undefined

  return (
    <div className={clsx('flex flex-col space-y-4 relative', className)}>
      <Button onClick={handleClickContact}>Contact my Account Manager</Button>
      <EmailInput
        enabled={enabled}
        email={emailAddress}
        onChange={handleChangeEmail}
        onFocus={handleFocusEmail}
        onBlur={handleBlurEmail}
        successful={successful}
        onSubmit={onRetry || handleSubmitEmail}
        message={message}
        sending={sending}
      />
      <StatusMessage className="absolute translate-y-full -bottom-2" type={message?.type} retry={onRetry}>
        {message?.text}
      </StatusMessage>
    </div>
  )
}
